
import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Users, Bike, ArrowRight, ChevronRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const HomePage: React.FC = () => {
  const solutions = [
    {
      title: '태양광발전 통합 지원',
      icon: <Sun className="h-8 w-8 text-btBlue" />,
      desc: '신재생에너지 보급 확대를 위한 전문 매칭 및 컨설팅 플랫폼',
      link: '/solutions#solar-all',
      color: 'blue'
    },
    {
      title: '주민참여형 태양광',
      icon: <Users className="h-8 w-8 text-btGreen" />,
      desc: '지역 주민과 함께 이익을 나누는 지속가능한 에너지 거뮤니티',
      link: '/solutions#citizen-solar',
      color: 'green'
    },
    {
      title: '자전거 활성화 플랫폼',
      icon: <Bike className="h-8 w-8 text-btOrange" />,
      desc: '시민의 기후행동을 데이터로 가치화하는 탄소중립 보상 플랫폼',
      link: '/solutions#bike-platform',
      color: 'orange'
    }
  ];

  return (
    <div className="overflow-hidden pt-16">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#f0f4f8] overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-btBlue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-btOrange/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 py-20 lg:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-btBlue/10 text-btBlue rounded-full text-sm font-bold mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-btBlue opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-btBlue"></span>
                </span>
                Next-Gen Climate Tech Solutions
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-gray-900 leading-[1.1] mb-8">
                세상을 바꾸는 <br />
                <span className="gradient-text">기후 기술,</span> <br />
                블루테라
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-12 leading-relaxed max-w-lg">
                블루테라는 IT 기술로 탄소중립의 장벽을 낮춥니다. <br />
                에너지 전환부터 시민 실천까지, 실질적인 기후위기 대응 솔루션을 제공합니다.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/solutions" className="px-10 py-4 bg-btBlue text-white rounded-full font-bold hover:bg-btBlue/90 hover:shadow-xl transition-all flex items-center gap-3">
                  서비스 둘러보기 <ArrowRight className="h-5 w-5" />
                </Link>
                <Link to="/contact" className="px-10 py-4 bg-white text-btBlue border-2 border-btBlue/20 rounded-full font-bold hover:bg-gray-50 transition-all">
                  비즈니스 협업 문의
                </Link>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
                <img 
                  src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?q=80&w=2070&auto=format&fit=crop" 
                  alt="Climate Action" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-btBlue/60 to-transparent"></div>
                <div className="absolute bottom-10 left-10 text-white">
                  <div className="text-4xl font-black mb-2">1,250 tons</div>
                  <div className="text-lg font-medium opacity-90">누적 탄소 저감량</div>
                </div>
              </div>
              
              {/* Floating Element */}
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border border-gray-100 hidden sm:block">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-btOrange/10 rounded-xl"><TrendingUp className="text-btOrange" /></div>
                  <div>
                    <div className="text-sm font-bold text-gray-400">성장률</div>
                    <div className="text-xl font-black text-gray-900">+124%</div>
                  </div>
                </div>
                <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                  <div className="bg-btBlue h-full w-4/5"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
            <div className="max-w-2xl">
              <h2 className="text-sm font-black text-btBlue uppercase tracking-[0.2em] mb-4">OUR SERVICES</h2>
              <h3 className="text-4xl font-black text-gray-900 mb-6 leading-tight">
                블루테라가 제시하는 <br />기후 위기 대응의 3가지 핵심 축
              </h3>
            </div>
            <Link to="/solutions" className="group flex items-center gap-2 text-btBlue font-bold hover:text-btOrange transition-all">
              전체 서비스 보기 <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -12 }}
                className="group relative p-10 bg-white rounded-[32px] border border-gray-100 shadow-sm hover:shadow-2xl transition-all overflow-hidden"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-bt${item.color.charAt(0).toUpperCase() + item.color.slice(1)}/5 rounded-bl-full`}></div>
                <div className="mb-10 p-5 bg-gray-50 inline-block rounded-2xl group-hover:bg-btBlue group-hover:text-white transition-all duration-300">
                  {item.icon}
                </div>
                <h4 className="text-2xl font-black text-gray-900 mb-5">{item.title}</h4>
                <p className="text-gray-600 mb-10 leading-relaxed">{item.desc}</p>
                <Link to={item.link} cclassName="px-10 py-4 bg-btBlue text-white rounded-full font-bold hover:bg-btBlue/90 hover:shadow-xl transition-all flex items-center gap-3">
                  자세히 보기 <ChevronRight  className="h-5 w-5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* (삭제) Partner & Clients 구간 */}
    </div>
  );
};

export default HomePage;
